import { Menu as AMenu } from '@ark-ui/solid/menu';
import { IconCheck } from '@bbr/icons/check-outline';
import { IconChevronRight } from '@bbr/icons/chevron-right-outline';
import { IconCircleCheck } from '@bbr/icons/circle-check-outline';
import { splitProps } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Link } from '../components/link.tsx';
import { Button } from '../forms/button.tsx';
import type { ComponentProps } from 'solid-js';

export function Menu(props: ComponentProps<typeof AMenu.Root>) {
	return <AMenu.Root {...props} />;
}

Menu.Trigger = (props: ComponentProps<typeof AMenu.Trigger>) => {
	return <AMenu.Trigger {...props} />;
};

Menu.ButtonTrigger = (props: ComponentProps<typeof Button>) => {
	return (
		<Menu.Trigger
			asChild={(p) => (
				// @ts-expect-error too complex
				<Button {...p()} {...props} />
			)}
		/>
	);
};

Menu.Content = (props: ComponentProps<typeof AMenu.Content>) => {
	return (
		<Portal>
			<AMenu.Positioner>
				<AMenu.Content
					{...props}
					class="z-50 flex max-h-(--available-height) max-w-(--available-width) flex-col overscroll-contain rounded-lg border border-zinc-200 bg-white p-2 shadow-md backdrop-blur duration-100 outline-none fade-out data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top-25 data-[state=closed]:zoom-out-50 data-[state=open]:animate-in data-[state=open]:duration-100 data-[state=open]:slide-in-from-top-25 data-[state=open]:zoom-in-50 data-[state=open]:fade-in dark:border-zinc-800 dark:bg-zinc-950"
				>
					<AMenu.Arrow>
						<AMenu.ArrowTip class="border-s border-t border-zinc-200 dark:border-zinc-800" />
					</AMenu.Arrow>
					{props.children}
				</AMenu.Content>
			</AMenu.Positioner>
		</Portal>
	);
};

Menu.TriggerItem = (props: ComponentProps<typeof AMenu.TriggerItem>) => {
	return (
		<AMenu.TriggerItem
			{...props}
			class="relative z-10 flex w-full min-w-32 grow cursor-pointer flex-nowrap items-center justify-between gap-x-2 rounded px-2 py-1.5 font-medium whitespace-nowrap transition-all duration-200 outline-none active:bg-yellow-100 disabled:bg-transparent disabled:opacity-50 data-highlighted:bg-yellow-500 data-highlighted:text-zinc-950"
		>
			{props.children}
			<IconChevronRight />
		</AMenu.TriggerItem>
	);
};

Menu.ItemGroup = (props: ComponentProps<typeof AMenu.ItemGroup>) => (
	<AMenu.ItemGroup {...props} class="flex flex-col gap-1" />
);
Menu.ItemGroupLabel = (props: ComponentProps<typeof AMenu.ItemGroupLabel>) => (
	<AMenu.ItemGroupLabel {...props} class="flex items-center justify-start gap-x-2 px-2 pt-1.5 pb-0.5 font-semibold" />
);

Menu.Item = (props: ComponentProps<typeof AMenu.Item>) => {
	return (
		<AMenu.Item
			{...props}
			class="relative z-10 flex w-full min-w-32 grow cursor-pointer flex-nowrap items-center justify-start gap-x-2 rounded px-2 py-1.5 font-medium whitespace-nowrap transition-all duration-200 outline-none active:bg-yellow-100 disabled:bg-transparent disabled:opacity-50 data-highlighted:bg-yellow-500 data-highlighted:text-zinc-950"
		/>
	);
};

Menu.LinkItem = (props: ComponentProps<typeof Link> & { value: string }) => {
	const [, link] = splitProps(props, ['value']);
	return <Menu.Item value={props.value} asChild={(p) => <Link {...p()} {...link} />} />;
};

Menu.Separator = (props: ComponentProps<typeof AMenu.Separator>) => {
	return <AMenu.Separator {...props} class="my-1 border-zinc-300 dark:border-zinc-700" />;
};

Menu.RadioItemGroup = AMenu.RadioItemGroup;
Menu.RadioItem = (props: ComponentProps<typeof AMenu.RadioItem>) => {
	return (
		<AMenu.RadioItem
			{...props}
			class="relative z-10 flex w-full min-w-32 grow cursor-pointer flex-nowrap items-center justify-start gap-x-2 rounded px-2 py-1.5 font-medium whitespace-nowrap transition-all duration-200 outline-none active:bg-yellow-100 disabled:bg-transparent disabled:opacity-50 aria-checked:text-yellow-500 data-highlighted:bg-yellow-500 data-highlighted:text-zinc-950"
		>
			<div class="w-4">
				<AMenu.ItemIndicator>
					<IconCircleCheck />
				</AMenu.ItemIndicator>
			</div>
			<AMenu.ItemText>{props.children}</AMenu.ItemText>
		</AMenu.RadioItem>
	);
};

Menu.CheckboxItem = (props: ComponentProps<typeof AMenu.CheckboxItem>) => {
	return (
		<AMenu.CheckboxItem
			{...props}
			class="relative z-10 flex w-full min-w-32 grow cursor-pointer flex-nowrap items-center justify-start gap-x-2 rounded px-2 py-1.5 font-medium whitespace-nowrap transition-all duration-200 outline-none active:bg-yellow-100 disabled:bg-transparent disabled:opacity-50 aria-checked:text-yellow-500 data-highlighted:bg-yellow-500 data-highlighted:text-zinc-950"
		>
			<div class="w-4">
				<AMenu.ItemIndicator>
					<IconCheck />
				</AMenu.ItemIndicator>
			</div>
			<AMenu.ItemText>{props.children}</AMenu.ItemText>
		</AMenu.CheckboxItem>
	);
};
